import React from 'react';
import styled, { css } from 'styled-components';
import TipBanner from '~/components/organism/TipBanner';
import ChatLink from '~/components/organism/TipBanner/ChatLink';
import Input from '~/components/molecule/Input';
import Dropdown from '~/components/molecule/Dropdown';
import TextButton from '~/components/atom/TextButton';
import Checkbox from '~/components/molecule/Checkbox';
import { ZapierField, ZapierFieldType } from '~/graphql/types';
import withIconTooltip from '~/hocs/withIconTooltip';
import type { ErrorObj } from '../../types';
import TEST_ID from './index.testid';
import JustificationContainer, {
  Props as JustificationProps,
} from '~/components/atom/JustificationContainer';
import { Heading4, Variant } from '~/components/atom/Typography';
import { isNil } from 'ramda';

export type OnChangeValue = {
  label?: string;
  key?: string;
  type?: ZapierFieldType;
  defaultString?: string | null;
  // Allow string for number to allow clearing the field
  defaultNumber?: string | number | null;
  defaultBoolean?: boolean | null;
};

export type Props = {
  /** Current active Zapier field  */
  field: Omit<ZapierField, '__typename'> | undefined;

  errors: ErrorObj | undefined;

  /** Save changes made in Zapier field  */
  onChange: (newValue: OnChangeValue) => void;

  /** Delete Zapier field  */
  onDelete: () => void;

  /** Close field editing section  */
  onClose: () => void;
};

const text = {
  tipHeader: 'Wat zijn velden?',
  tip: (
    <>
      Met velden bepaal je welke gegevens je beschikbaar wil maken voor de
      koppeling met Zapier. <br />
      <br />
      <ChatLink linkText="Start een chat met ons." />
    </>
  ),
  nameLabel: 'Naam',
  nameTooltip: 'Naam van het veld',
  keyLabel: 'Key',
  keyTooltip: 'Key voor het veld',
  typeLabel: 'Type',
  typeTooltip: 'Type voor het veld',
  defaultLabel: 'Standaard waarde',
  defaultTooltip: 'Optionele standaard waarde voor dit veld',
  useStandardValueLabel: 'Gebruik standaard waarde',
  defaultStringLabel: 'Standaard tekst',
  defaultNumberLabel: 'Standaard nummer',
  defaultBooleanLabel: 'Standaard boolean',
  deleteButtonLabel: 'Verwijder dit veld',
  booleanTrueLabel: 'Waar',
  booleanFalseLabel: 'Niet waar',
};

const FieldEditingSection: React.FCC<Props> = ({
  dataTestId,
  field,
  onChange,
  onDelete,
  onClose,
  errors = {
    key: [],
    label: [],
    defaultString: [],
    defaultNumber: [],
    defaultBoolean: [],
  },
  ...rest
}) => {
  if (!field)
    return (
      <Container>
        <TipBanner
          id="field-editing-section-tip"
          dismissible={false}
          withoutBorder
          headerText={text.tipHeader}
        >
          {text.tip}
        </TipBanner>
      </Container>
    );

  const { label, key, type, defaultString, defaultNumber, defaultBoolean } =
    field;

  // Check if default value feature is enabled (allows empty string for strings to show input)
  const isDefaultEnabled =
    (type === ZapierFieldType.String && !isNil(defaultString)) ||
    (type === ZapierFieldType.Number && !isNil(defaultNumber)) ||
    (type === ZapierFieldType.Boolean && !isNil(defaultBoolean));

  const inputWithLabelGroupProps: JustificationProps = {
    width: '100%',
    direction: 'column',
    align: 'start',
    justification: 'center',
  };

  const onToggleStandardValue = (checked: boolean) => {
    switch (type) {
      case ZapierFieldType.String:
        onChange({ defaultString: checked ? '' : null });
        break;
      case ZapierFieldType.Number:
        onChange({ defaultNumber: checked ? 0 : null });
        break;
      case ZapierFieldType.Boolean:
        onChange({ defaultBoolean: checked ? false : null });
        break;
      default:
        break;
    }
  };

  const onStandardValueChange = (value: string, fieldType: ZapierFieldType) => {
    switch (fieldType) {
      case ZapierFieldType.String:
        onChange({ defaultString: value });
        break;
      case ZapierFieldType.Number:
        // allow clearing the field, this will show validation error
        const numValue = value === '' ? '' : parseFloat(value);
        onChange({
          defaultNumber: numValue,
        });
        break;
      case ZapierFieldType.Boolean:
        onChange({ defaultBoolean: value === 'true' });
        break;
      default:
        break;
    }
  };

  return (
    <Container data-testid={dataTestId} {...rest} gap="base" direction="column">
      <CloseButton icon="close" onClick={onClose} padding={[null]} />
      <JustificationContainer {...inputWithLabelGroupProps}>
        <LabelWithTooltip tooltipMessage={text.nameTooltip}>
          {text.nameLabel}
        </LabelWithTooltip>
        <Input
          width="100%"
          value={label}
          onChange={e => onChange({ label: e.target.value })}
          externalErrors={errors.label ?? []}
          dataTestId={TEST_ID.INPUT_NAME}
        />
      </JustificationContainer>
      <JustificationContainer {...inputWithLabelGroupProps}>
        <LabelWithTooltip tooltipMessage={text.keyTooltip}>
          {text.keyLabel}
        </LabelWithTooltip>
        <Input
          width="100%"
          value={key}
          onChange={e => onChange({ key: e.target.value })}
          externalErrors={errors.key ?? []}
          dataTestId={TEST_ID.INPUT_KEY}
        />
      </JustificationContainer>

      <JustificationContainer {...inputWithLabelGroupProps}>
        <LabelWithTooltip tooltipMessage={text.typeTooltip}>
          {text.typeLabel}
        </LabelWithTooltip>
        <Dropdown
          options={fieldTypeOptions}
          selectedOptionIdx={fieldTypeOptions.findIndex(
            option => option.payload === type,
          )}
          onChange={({ option }) => {
            onChange({
              type: option.payload,
              defaultString: null,
              defaultNumber: null,
              defaultBoolean: null,
            });
          }}
        />
      </JustificationContainer>

      <JustificationContainer {...inputWithLabelGroupProps} gap="base">
        <LabelWithTooltip tooltipMessage={text.defaultTooltip}>
          {text.defaultLabel}
        </LabelWithTooltip>
        <Checkbox
          value={isDefaultEnabled}
          onChange={() => onToggleStandardValue(!isDefaultEnabled)}
          label={text.useStandardValueLabel}
          dataTestId={TEST_ID.CHECKBOX_DEFAULT}
        />
      </JustificationContainer>

      {isDefaultEnabled && type === ZapierFieldType.String && (
        <JustificationContainer {...inputWithLabelGroupProps}>
          <LabelWithTooltip tooltipMessage={text.defaultStringLabel}>
            {text.defaultStringLabel}
          </LabelWithTooltip>
          <Input
            width="100%"
            value={defaultString ?? ''}
            onChange={e =>
              onStandardValueChange(e.target.value, ZapierFieldType.String)
            }
            externalErrors={errors.defaultString ?? []}
            dataTestId={TEST_ID.INPUT_DEFAULT_STRING}
          />
        </JustificationContainer>
      )}

      {isDefaultEnabled && type === ZapierFieldType.Number && (
        <JustificationContainer {...inputWithLabelGroupProps}>
          <LabelWithTooltip tooltipMessage={text.defaultNumberLabel}>
            {text.defaultNumberLabel}
          </LabelWithTooltip>
          <Input
            width="100%"
            type="number"
            value={defaultNumber ?? ''}
            onChange={e =>
              onStandardValueChange(e.target.value, ZapierFieldType.Number)
            }
            externalErrors={errors.defaultNumber ?? []}
            dataTestId={TEST_ID.INPUT_DEFAULT_NUMBER}
          />
        </JustificationContainer>
      )}

      {isDefaultEnabled && type === ZapierFieldType.Boolean && (
        <JustificationContainer {...inputWithLabelGroupProps}>
          <LabelWithTooltip tooltipMessage={text.defaultBooleanLabel}>
            {text.defaultBooleanLabel}
          </LabelWithTooltip>
          <Dropdown
            options={[
              { key: 'true', label: text.booleanTrueLabel, payload: 'true' },
              { key: 'false', label: text.booleanFalseLabel, payload: 'false' },
            ]}
            selectedOptionIdx={
              defaultBoolean === true ? 0 : defaultBoolean === false ? 1 : 0
            }
            onChange={({ option }) =>
              onStandardValueChange(option.payload, ZapierFieldType.Boolean)
            }
            error={errors.defaultBoolean?.[0]}
          />
        </JustificationContainer>
      )}

      <TextButton
        label={text.deleteButtonLabel}
        appearance="danger"
        onClick={onDelete}
        dataTestId={TEST_ID.DELETE_FIELD_BUTTON}
      />
    </Container>
  );
};

const LabelWithTooltip = withIconTooltip(Heading4, {
  variant: Variant.primary,
  margin: [null],
});

const fieldTypeOptions = [
  {
    key: 'string',
    label: 'String (tekst)',
    payload: ZapierFieldType.String,
  },
  {
    key: 'number',
    label: 'Nummer',
    payload: ZapierFieldType.Number,
  },
  {
    key: 'boolean',
    label: 'Boolean',
    payload: ZapierFieldType.Boolean,
  },
];

const Container = styled(JustificationContainer)<{}>`
  position: relative;
  flex: 1;

  ${({ theme }) => css`
    border-radius: ${theme.getTokens().border.radius.s};
    box-shadow: ${theme.getTokens().boxShadow.card};
    background-color: ${theme.color('white')};
    padding: ${theme.space('base')};
    margin-left: ${theme.space('base')};
  `}
`;

const CloseButton = styled(TextButton)<{}>(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.space('xxs')};
    right: ${theme.space('xxs')};
  `,
);

export default FieldEditingSection;
